"use client";

import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const COLS = 15;
const ROWS = 11;
const AUTO_SPEED = 150; // ms par déplacement (démo automatique)
const MANUAL_SPEED = 190; // ms par déplacement (un peu plus lent quand on joue)

const RIGHT = { x: 1, y: 0 };
const LEFT = { x: -1, y: 0 };
const UP = { x: 0, y: -1 };
const DOWN = { x: 0, y: 1 };

const startSnake = () => [
    { x: 7, y: 5 },
    { x: 6, y: 5 },
    { x: 5, y: 5 },
];

function spawnFood(snake) {
    const occupied = new Set(snake.map((s) => `${s.x},${s.y}`));
    const free = [];
    for (let y = 0; y < ROWS; y++) {
        for (let x = 0; x < COLS; x++) {
            if (!occupied.has(`${x},${y}`)) free.push({ x, y });
        }
    }
    return free[Math.floor(Math.random() * free.length)] || { x: 0, y: 0 };
}

// IA gloutonne : se rapproche de la pomme en évitant les murs et son corps.
function autoDir(snake, food, curDir) {
    const head = snake[0];
    const occupied = new Set(snake.slice(0, -1).map((s) => `${s.x},${s.y}`));
    const candidates = [RIGHT, LEFT, UP, DOWN]
        .filter((d) => !(d.x === -curDir.x && d.y === -curDir.y))
        .map((d) => {
            const nx = head.x + d.x;
            const ny = head.y + d.y;
            const blocked =
                nx < 0 ||
                ny < 0 ||
                nx >= COLS ||
                ny >= ROWS ||
                occupied.has(`${nx},${ny}`);
            return {
                d,
                blocked,
                dist: Math.abs(nx - food.x) + Math.abs(ny - food.y),
            };
        });
    const safe = candidates.filter((c) => !c.blocked).sort((a, b) => a.dist - b.dist);
    return safe.length ? safe[0].d : curDir;
}

// Joli serpent figé pour les utilisateurs en "mouvement réduit".
const staticSnake = () => [
    { x: 9, y: 5 },
    { x: 8, y: 5 },
    { x: 7, y: 5 },
    { x: 6, y: 5 },
    { x: 6, y: 6 },
    { x: 6, y: 7 },
];

export default function PythonSnakeGame() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-120px" });

    const snakeRef = useRef(startSnake());
    const dirRef = useRef(RIGHT);
    const pendingRef = useRef(RIGHT);
    const foodRef = useRef(spawnFood(startSnake()));
    const scoreRef = useRef(0);
    const bestRef = useRef(0);
    const statusRef = useRef("running"); // running | over | idle
    const manualRef = useRef(false);
    const restartRef = useRef(null);
    const loopRef = useRef(null);

    const [, setFrame] = useState(0);
    const render = () => setFrame((f) => (f + 1) % 1e9);

    function resetBoard() {
        const snake = startSnake();
        snakeRef.current = snake;
        dirRef.current = RIGHT;
        pendingRef.current = RIGHT;
        foodRef.current = spawnFood(snake);
        scoreRef.current = 0;
        statusRef.current = "running";
        render();
    }

    function endGame() {
        statusRef.current = "over";
        if (scoreRef.current > bestRef.current) bestRef.current = scoreRef.current;
        render();
        if (!manualRef.current) {
            restartRef.current = setTimeout(resetBoard, 1500);
        }
    }

    function step() {
        if (statusRef.current !== "running") return;

        let dir = manualRef.current ? pendingRef.current : autoDir(snakeRef.current, foodRef.current, dirRef.current);
        if (
            snakeRef.current.length > 1 &&
            dir.x === -dirRef.current.x &&
            dir.y === -dirRef.current.y
        ) {
            dir = dirRef.current;
        }
        dirRef.current = dir;

        const head = snakeRef.current[0];
        const nx = head.x + dir.x;
        const ny = head.y + dir.y;

        if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) return endGame();

        const willEat = nx === foodRef.current.x && ny === foodRef.current.y;
        const body = willEat ? snakeRef.current : snakeRef.current.slice(0, -1);
        if (body.some((s) => s.x === nx && s.y === ny)) return endGame();

        const next = [{ x: nx, y: ny }, ...snakeRef.current];
        if (willEat) {
            scoreRef.current += 1;
            foodRef.current = spawnFood(next);
        } else {
            next.pop();
        }
        snakeRef.current = next;
        render();
    }

    function setDirection(d) {
        if (statusRef.current === "over") return;
        manualRef.current = true;
        if (statusRef.current === "idle") statusRef.current = "running";
        if (
            snakeRef.current.length > 1 &&
            d.x === -dirRef.current.x &&
            d.y === -dirRef.current.y
        ) {
            render();
            return;
        }
        pendingRef.current = d;
        render();
    }

    function backToAuto() {
        manualRef.current = false;
        if (statusRef.current === "over") resetBoard();
        else render();
    }

    function replay() {
        resetBoard();
    }

    function onKeyDown(e) {
        // On ne capte pas les touches quand l'utilisateur tape dans un champ.
        const tag = (e.target && e.target.tagName) || "";
        if (/^(input|textarea|select)$/i.test(tag) || e.target?.isContentEditable) {
            return;
        }
        const k = e.key && e.key.length === 1 ? e.key.toLowerCase() : e.key;
        const map = {
            ArrowUp: UP,
            ArrowDown: DOWN,
            ArrowLeft: LEFT,
            ArrowRight: RIGHT,
            w: UP,
            z: UP,
            s: DOWN,
            a: LEFT,
            q: LEFT,
            d: RIGHT,
        };
        const dir = map[k];
        if (!dir) return;
        e.preventDefault();
        if (statusRef.current === "over") replay();
        setDirection(dir);
    }

    // Démarrage / arrêt de la boucle de jeu selon la visibilité.
    useEffect(() => {
        if (reduce) {
            // Mouvement réduit : plateau figé, aucune animation auto.
            // Le jeu ne démarre qu'à la première action de l'utilisateur.
            snakeRef.current = staticSnake();
            foodRef.current = { x: 11, y: 3 };
            statusRef.current = "idle";
            manualRef.current = true;
            render();
        }
        if (!inView) return;

        // Reprise propre si on revient sur la section après un game over en démo.
        if (statusRef.current === "over" && !manualRef.current && !reduce) {
            restartRef.current = setTimeout(resetBoard, 1500);
        }

        // Boucle à vitesse variable : un peu plus lente quand on joue
        // (le temps de réagir), plus nerveuse en démo automatique.
        let stopped = false;
        const loop = () => {
            if (stopped) return;
            step();
            loopRef.current = setTimeout(
                loop,
                manualRef.current ? MANUAL_SPEED : AUTO_SPEED,
            );
        };
        loopRef.current = setTimeout(
            loop,
            manualRef.current ? MANUAL_SPEED : AUTO_SPEED,
        );

        // Le clavier pilote le serpent dès que la section est à l'écran,
        // sans devoir cliquer sur la grille au préalable.
        window.addEventListener("keydown", onKeyDown);

        return () => {
            stopped = true;
            if (loopRef.current) clearTimeout(loopRef.current);
            if (restartRef.current) clearTimeout(restartRef.current);
            window.removeEventListener("keydown", onKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, reduce]);

    const snake = snakeRef.current;
    const food = foodRef.current;
    const headKey = `${snake[0].x},${snake[0].y}`;
    const bodyIndex = new Map(snake.map((s, i) => [`${s.x},${s.y}`, i]));
    const over = statusRef.current === "over";

    return (
        <section id="python-snake" className="pt-16" ref={ref}>
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Python_snake_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Python_snake_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon icon="logos:python" className="w-3.5 h-3.5" aria-hidden="true" />
                        snake.py
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Python_snake_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-6 sm:p-8 flex flex-col items-center">
                    {/* Plateau */}
                    <div
                        className="relative w-full max-w-[540px] rounded-2xl ring-1 ring-white/10 bg-black/30 overflow-hidden"
                        style={{ aspectRatio: `${COLS} / ${ROWS}` }}
                    >
                        {/* Quadrillage */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                backgroundImage:
                                    "linear-gradient(to right, rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.045) 1px, transparent 1px)",
                                backgroundSize: `${100 / COLS}% ${100 / ROWS}%`,
                            }}
                        />

                        {/* Cellules */}
                        <div
                            className="absolute inset-0 grid"
                            style={{
                                gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                                gridTemplateRows: `repeat(${ROWS}, 1fr)`,
                            }}
                        >
                            {Array.from({ length: COLS * ROWS }).map((_, idx) => {
                                const x = idx % COLS;
                                const y = Math.floor(idx / COLS);
                                const key = `${x},${y}`;
                                const isHead = key === headKey;
                                const bi = bodyIndex.get(key);
                                const isFood = food.x === x && food.y === y;

                                return (
                                    <div key={idx} className="p-[1.5px]">
                                        {isHead ? (
                                            <div className="w-full h-full rounded-[5px] bg-lime-300 shadow-[0_0_12px] shadow-lime-400/60" />
                                        ) : bi !== undefined ? (
                                            <div
                                                className="w-full h-full rounded-[4px] bg-emerald-400"
                                                style={{
                                                    opacity: Math.max(
                                                        0.45,
                                                        1 - bi * 0.045,
                                                    ),
                                                }}
                                            />
                                        ) : isFood ? (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="block w-[72%] h-[72%] rounded-full bg-red-500 shadow-[0_0_10px] shadow-red-500/60 animate-pulse" />
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Overlay Game Over */}
                        {over && (
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-bgDark-900/80 backdrop-blur-[2px]">
                                <div className="text-2xl font-bold text-white font-sourceSans">
                                    {t("Python_snake_gameover")}
                                </div>
                                <div className="font-mono text-sky-300 text-sm">
                                    {t("Python_snake_score")} : {scoreRef.current}
                                </div>
                                {manualRef.current ? (
                                    <button
                                        type="button"
                                        onClick={replay}
                                        className="mt-1 inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 transition-colors px-5 py-2 text-sm font-semibold text-white"
                                    >
                                        <SolarIcon
                                            name="restart-bold-duotone"
                                            className="w-4 h-4"
                                        />
                                        {t("Python_snake_replay")}
                                    </button>
                                ) : (
                                    <div className="text-xs text-gray-400 font-mono">
                                        {t("Python_snake_restarting")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Barre d'état + contrôles */}
                    <div className="mt-5 w-full max-w-[540px] flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 font-mono text-sm">
                            <span className="text-white">
                                {t("Python_snake_score")}{" "}
                                <b className="text-lime-300">{scoreRef.current}</b>
                            </span>
                            <span className="text-gray-500">
                                {t("Python_snake_best")}{" "}
                                <b className="text-gray-300">{bestRef.current}</b>
                            </span>
                        </div>
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ${
                                manualRef.current
                                    ? "bg-amber-500/15 ring-amber-400/30 text-amber-300"
                                    : "bg-sky-500/15 ring-sky-400/30 text-sky-300"
                            }`}
                        >
                            {manualRef.current
                                ? t("Python_snake_manual")
                                : t("Python_snake_auto")}
                        </span>
                    </div>

                    {/* D-pad + retour auto */}
                    <div className="mt-5 flex items-center gap-8">
                        <div className="grid grid-cols-3 grid-rows-3 gap-1.5 w-[132px]">
                            <span />
                            <DPadButton
                                onClick={() => setDirection(UP)}
                                icon="alt-arrow-up-bold"
                                label={t("Python_snake_up")}
                            />
                            <span />
                            <DPadButton
                                onClick={() => setDirection(LEFT)}
                                icon="alt-arrow-left-bold"
                                label={t("Python_snake_left")}
                            />
                            <span className="flex items-center justify-center">
                                <SolarIcon
                                    name="gamepad-bold-duotone"
                                    className="w-5 h-5 text-gray-600"
                                />
                            </span>
                            <DPadButton
                                onClick={() => setDirection(RIGHT)}
                                icon="alt-arrow-right-bold"
                                label={t("Python_snake_right")}
                            />
                            <span />
                            <DPadButton
                                onClick={() => setDirection(DOWN)}
                                icon="alt-arrow-down-bold"
                                label={t("Python_snake_down")}
                            />
                            <span />
                        </div>

                        <div className="flex flex-col gap-2 text-xs text-gray-400 font-geistSans max-w-[170px]">
                            <span>{t("Python_snake_keys")}</span>
                            {manualRef.current && !reduce && (
                                <button
                                    type="button"
                                    onClick={backToAuto}
                                    className="inline-flex items-center gap-1.5 self-start rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-sky-300 transition-colors"
                                >
                                    <SolarIcon
                                        name="refresh-bold-duotone"
                                        className="w-3.5 h-3.5"
                                    />
                                    {t("Python_snake_back_auto")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Python_snake_caption")}
            </p>
        </section>
    );
}

function DPadButton({ onClick, icon, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] hover:bg-sky-500/20 ring-1 ring-white/10 hover:ring-sky-400/40 text-gray-200 hover:text-sky-200 active:scale-95 transition"
        >
            <SolarIcon name={icon} className="w-5 h-5" />
        </button>
    );
}
